// API configuration and endpoints
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const API_BASE_URL_SRN_TO_NL = import.meta.env.VITE_API_URL_SRN_TO_NL || '/api-srn-nl';

// API endpoint URLs for NL → SRN (default)
export const API_ENDPOINTS = {
  translate: `${API_BASE_URL}/translate`,
  translatePostEdit: `${API_BASE_URL}/translate/post-edit`,
  health: `${API_BASE_URL}/health`,
  metrics: `${API_BASE_URL}/metrics`,
  feedback: `${API_BASE_URL}/feedback`,
  analytics: `${API_BASE_URL}/analytics`,
};

// API endpoint URLs for SRN → NL
export const API_ENDPOINTS_SRN_TO_NL = {
  translate: `${API_BASE_URL_SRN_TO_NL}/translate`,
  translatePostEdit: `${API_BASE_URL_SRN_TO_NL}/translate/post-edit`,
  health: `${API_BASE_URL_SRN_TO_NL}/health`,
  feedback: `${API_BASE_URL_SRN_TO_NL}/feedback`,
};

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 60000; // 60 seconds for translation requests

// API Key for local development (bypasses Azure Auth)
const API_KEY = import.meta.env.VITE_API_KEY;

/**
 * PRIVACY-BY-DESIGN: Fetch wrapper with timeout and error handling
 * 
 * AUTHENTICATION VS STORAGE:
 * ==========================
 * We send X-MS-CLIENT-PRINCIPAL headers for AUTHENTICATION (access control),
 * but the backend does NOT store user identity with translations/feedback.
 * 
 * The backend:
 * 1. Validates the header to ensure user is authenticated
 * 2. Extracts provider_sub for HMAC rate limiting (one-way hash)
 * 3. DISCARDS user identity before saving content
 * 
 * Result: Content is anonymous, but access is controlled.
 */
const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  // Build authentication headers
  let authHeaders = {};
  
  if (API_KEY) {
    // Local development: use API key
    authHeaders['X-API-Key'] = API_KEY;
    console.log('Using API key for authentication (local dev mode)');
  } else {
    // Production: Get authentication from Azure Static Web Apps
    // This is needed for the backend to validate the user is authenticated
    try {
      const authResponse = await fetch('/.auth/me');
      if (authResponse.ok) {
        const authData = await authResponse.json();
        if (authData.clientPrincipal) {
          // Create X-MS-CLIENT-PRINCIPAL header (base64 encoded JSON)
          // This is the format Azure Static Web Apps uses
          const principalData = {
            userId: authData.clientPrincipal.userId,
            userDetails: authData.clientPrincipal.userDetails,
            identityProvider: authData.clientPrincipal.identityProvider,
            userRoles: authData.clientPrincipal.userRoles || ['anonymous', 'authenticated'],
            claims: authData.clientPrincipal.claims || []
          };
          
          // Base64 encode the principal data
          const principalJson = JSON.stringify(principalData);
          const principalBase64 = btoa(principalJson);
          
          // PRIVACY NOTE: These headers are used for AUTHENTICATION only.
          // The backend validates them but does NOT store user identity with content.
          authHeaders['X-MS-CLIENT-PRINCIPAL'] = principalBase64;
          authHeaders['X-MS-CLIENT-PRINCIPAL-ID'] = authData.clientPrincipal.userId;
          authHeaders['X-MS-CLIENT-PRINCIPAL-IDP'] = authData.clientPrincipal.identityProvider;
          // NOTE: We intentionally do NOT send user name/email - not needed for auth
        }
      }
    } catch (error) {
      console.log('Auth header setup failed:', error);
    }
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        ...authHeaders
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    
    throw error;
  }
};

/**
 * Translation API Service
 */
export class TranslationAPI {
  /**
   * Translate text between Dutch and Sranantongo
   * @param {string[]} texts - Array of texts to translate
   * @param {number} maxLength - Maximum length for translation
   * @param {string} direction - Translation direction: 'nl-to-srn' or 'srn-to-nl'
   * @returns {Promise<Object>} Translation response
   */
  static async translate(texts, maxLength = 512, direction = 'nl-to-srn') {
    let endpoint;
    if (direction === 'srn-to-nl') {
      // Use post-edit endpoint for srn-to-nl to include dictionary corrections
      endpoint = API_ENDPOINTS_SRN_TO_NL.translatePostEdit;
    } else {
      // Use post-edit endpoint for nl-to-srn to include heuristic corrections
      endpoint = API_ENDPOINTS.translatePostEdit;
    }
    
    console.log('Making translation request:', { texts, maxLength, direction });
    console.log('API endpoint:', endpoint);
    
    try {
      const response = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          texts,
          max_length: maxLength,
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        let errorMessage = 'Translation failed';
        try {
          const errorData = await response.json();
          console.log('Error response data:', errorData);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If we can't parse error JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Translation response:', data);
      
      // Validate response structure
      if (!data.results || !Array.isArray(data.results)) {
        throw new Error('Invalid response format from translation service');
      }

      return data;
    } catch (error) {
      console.error('Translation API error:', error);
      
      // Re-throw with more context if it's a network error
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to translation service. Please check your internet connection.');
      }
      
      throw error;
    }
  }

  /**
   * Check the health status of the translation service
   * @returns {Promise<Object>} Health status response
   */
  static async checkHealth() {
    try {
      const response = await fetchWithTimeout(API_ENDPOINTS.health, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Service health check failed: ${error.message}`);
    }
  }

  /**
   * Warm up the backend service with retry logic for cold starts
   * @returns {Promise<boolean>} True if service is ready, false if still warming up
   */
  static async warmUpService() {
    const maxRetries = 3;
    const retryDelay = 5000; // 5 seconds

    // Warm up both services in parallel
    const warmUpSingle = async (endpoint, serviceName) => {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`🔄 Warming up ${serviceName} (attempt ${attempt}/${maxRetries})...`);
          
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
          });

          if (response.ok) {
            console.log(`✅ ${serviceName} is ready`);
            return true;
          }
          
          console.log(`⚠️ ${serviceName} attempt ${attempt} failed, retrying in ${retryDelay/1000}s...`);
        } catch (error) {
          console.log(`⚠️ ${serviceName} attempt ${attempt} failed: ${error.message}`);
        }

        // Wait before retrying (except on last attempt)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
      return false;
    };

    // Warm up both services in parallel
    const [nlToSrnReady, srnToNlReady] = await Promise.all([
      warmUpSingle(API_ENDPOINTS.health, 'NL→SRN backend'),
      warmUpSingle(API_ENDPOINTS_SRN_TO_NL.health, 'SRN→NL backend'),
    ]);

    if (nlToSrnReady && srnToNlReady) {
      console.log('✅ Both backend services are ready');
      return true;
    } else {
      console.log('⚠️ Backend still warming up - translation requests may be slower');
      return nlToSrnReady || srnToNlReady; // Return true if at least one is ready
    }
  }

  /**
   * Get service metrics
   * @returns {Promise<Object>} Service metrics
   */
  static async getMetrics() {
    try {
      const response = await fetchWithTimeout(API_ENDPOINTS.metrics, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Metrics request failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to fetch service metrics: ${error.message}`);
    }
  }
}

/**
 * Feedback API Service
 */
export class FeedbackAPI {
  /**
   * Submit user feedback and rating for a translation
   * @param {Object} feedbackData - Feedback data object
   * @returns {Promise<Object>} Submission response
   */
  static async submitFeedback(feedbackData) {
    console.log('Submitting feedback to database:', feedbackData);
    
    try {
      const response = await fetchWithTimeout(API_ENDPOINTS.feedback, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        let errorMessage = 'Feedback submission failed';
        try {
          const errorData = await response.json();
          console.log('Feedback error response:', errorData);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Feedback submitted successfully:', data);
      
      return {
        success: true,
        message: 'Feedback submitted successfully',
        data: data,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Feedback API error:', error);
      
      // Re-throw with more context if it's a network error
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to feedback service. Please check your internet connection.');
      }
      
      throw error;
    }
  }

  /**
   * Get analytics data for translations and feedback
   * @returns {Promise<Object>} Analytics response
   */
  static async getAnalytics() {
    console.log('Fetching analytics data');
    
    try {
      const response = await fetchWithTimeout(API_ENDPOINTS.analytics, {
        method: 'GET',
      });

      if (!response.ok) {
        let errorMessage = 'Analytics request failed';
        try {
          const errorData = await response.json();
          console.log('Analytics error response:', errorData);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Analytics data received:', data);
      
      return data;
    } catch (error) {
      console.error('Analytics API error:', error);
      
      // Re-throw with more context if it's a network error
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to analytics service. Please check your internet connection.');
      }
      
      throw error;
    }
  }
}

/**
 * Session Storage Helper
 */
export class SessionStore {
  /**
   * Store translation data in session storage
   * Also clears any previous feedback submission state
   * @param {Object} data - Translation data to store
   */
  static storeTranslation(data) {
    try {
      sessionStorage.setItem('translationData', JSON.stringify(data));
      // Clear feedback submitted flag when storing new translation
      sessionStorage.removeItem('feedbackSubmitted');
    } catch (error) {
      console.error('Failed to store translation data:', error);
    }
  }

  /**
   * Retrieve translation data from session storage
   * @returns {Object|null} Translation data or null if not found
   */
  static getTranslation() {
    try {
      const data = sessionStorage.getItem('translationData');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to retrieve translation data:', error);
      return null;
    }
  }

  /**
   * Clear translation data from session storage
   */
  static clearTranslation() {
    try {
      sessionStorage.removeItem('translationData');
      sessionStorage.removeItem('feedbackSubmitted');
    } catch (error) {
      console.error('Failed to clear translation data:', error);
    }
  }

  /**
   * Mark feedback as submitted
   */
  static markFeedbackSubmitted() {
    try {
      sessionStorage.setItem('feedbackSubmitted', 'true');
    } catch (error) {
      console.error('Failed to mark feedback as submitted:', error);
    }
  }

  /**
   * Check if feedback was submitted
   * @returns {boolean} True if feedback was submitted
   */
  static isFeedbackSubmitted() {
    try {
      return sessionStorage.getItem('feedbackSubmitted') === 'true';
    } catch (error) {
      console.error('Failed to check feedback submission status:', error);
      return false;
    }
  }
}

export default TranslationAPI;